import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardMedia, 
  Stack,
  Chip,
  CardActionArea,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { formatDistanceToNow } from 'date-fns';
import { getArticles, updateArticleStatus, addTagToArticles } from '../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CreateArticleDialog from './CreateArticleDialog';
import AdaptiveImage from './AdaptiveImage';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  getTypeLabel,
  getSubtypeLabel,
  getStatusLabel,
  getStatusColor,
  ARTICLE_STATUSES
} from '../constants/articleTypes';

const isDev = process.env.NODE_ENV === 'development';

const ARTICLE_TYPES = {
  article: { label: 'Article' },
  long_social_post: { label: 'Long Social Post' },
  short_social_post: { label: 'Short Social Post' },
  image_carousel: { label: 'Image Carousel' },
  short_video: { label: 'Short Video' },
  long_video: { label: 'Long Video' }
};

const ARTICLE_SUBTYPES = {
  // Article subtypes
  how_to: { label: 'How-To Guide' },
  list_article: { label: 'List Article' },
  review: { label: 'Review' },
  opinion: { label: 'Opinion' },
  news: { label: 'News' },
  case_study: { label: 'Case Study' },
  
  // Short social post subtypes
  instagram_post: { label: 'Instagram Post' },
  x_post: { label: 'X/Twitter Post' },
  facebook_post: { label: 'Facebook Post' },
  linkedin_short: { label: 'LinkedIn Short' },
  threads_post: { label: 'Threads Post' },
  
  // Long social post subtypes
  linkedin_article: { label: 'LinkedIn Article' },
  facebook_long: { label: 'Facebook Long Post' },
  medium_story: { label: 'Medium Story' },
  x_thread: { label: 'X/Twitter Thread' },
  
  // Image carousel subtypes
  instagram_carousel: { label: 'Instagram Carousel' },
  linkedin_carousel: { label: 'LinkedIn Carousel' },
  facebook_carousel: { label: 'Facebook Carousel' },
  
  // Short video subtypes
  tiktok: { label: 'TikTok' },
  instagram_reel: { label: 'Instagram Reel' },
  youtube_short: { label: 'YouTube Short' },
  
  // Long video subtypes
  youtube_video: { label: 'YouTube Video' },
  vimeo_video: { label: 'Vimeo Video' },
  course_video: { label: 'Course Video' }
};

const ArticlesList = ({ projectId }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [addTagDialogOpen, setAddTagDialogOpen] = useState(false);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    loadArticles();
  }, [projectId]);

  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setCreateDialogOpen(true);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('create');
      navigate(`?${newSearchParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const loadArticles = async () => {
    try {
      const data = await getArticles(projectId);
      setArticles(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (articleId, newStatus) => {
    try {
      await updateArticleStatus(articleId, newStatus);
      await loadArticles(); // Reload the list to get updated data
    } catch (err) {
      setError('Error updating status: ' + err.message);
    }
  };

  const filteredArticles = articles.filter(article => {
    const search = searchTerm.toLowerCase();
    return (
      article.title.toLowerCase().includes(search) ||
      article.topic.toLowerCase().includes(search) ||
      article.tags?.some(tag => tag.toLowerCase().includes(search))
    );
  });

  const handleArticleCreated = async (newArticle) => {
    await loadArticles(); // Reload the list to include the new article
    if (newArticle?.id) {
      navigate(`/articles/${newArticle.id}`);
    }
  };

  const handleSelectArticle = (articleId) => {
    setSelectedArticles(prev => {
      if (prev.includes(articleId)) {
        return prev.filter(id => id !== articleId);
      } else {
        return [...prev, articleId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedArticles.length === filteredArticles.length) {
      setSelectedArticles([]);
    } else {
      setSelectedArticles(filteredArticles.map(article => article.id));
    }
  };

  const handleAddTag = async () => {
    try {
      const updatedArticles = await addTagToArticles(selectedArticles, newTag);
      setArticles(prevArticles => {
        return prevArticles.map(article => {
          const updatedArticle = updatedArticles.find(ua => ua.id === article.id);
          if (updatedArticle) {
            return {
              ...article,
              tags: updatedArticle.tags
            };
          }
          return article;
        });
      });
      
      setNewTag('');
      setAddTagDialogOpen(false);
      setSelectedArticles([]); // Clear selection
    } catch (err) {
      setError('Error adding tag: ' + err.message);
    }
  };

  if (loading) {
    return <Typography>Loading articles...</Typography>;
  }

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={2}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search articles by title, topic, or tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ 
          display: 'flex', 
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' }
        }}>
          {selectedArticles.length > 0 && (
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              onClick={() => setAddTagDialogOpen(true)}
              sx={{ flex: { sm: 1 } }}
            >
              Add Tag ({selectedArticles.length})
            </Button>
          )}
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ flex: { sm: 1 } }}
          >
            Create Content
          </Button>
        </Box>
      </Stack>

      {filteredArticles.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            checked={selectedArticles.length === filteredArticles.length}
            indeterminate={selectedArticles.length > 0 && selectedArticles.length < filteredArticles.length}
            onChange={handleSelectAll}
          />
          <Typography variant="body2" color="text.secondary">
            {selectedArticles.length} selected
          </Typography>
        </Box>
      )}
      
      {filteredArticles.length === 0 ? (
        <Typography color="text.secondary" align="center">
          No articles found matching your search.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {filteredArticles.map((article) => (
            <Card key={article.id}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  p: { xs: 1, sm: 0 },
                  pl: 1,
                  borderBottom: { xs: 1, sm: 0 },
                  borderColor: 'divider'
                }}>
                  <Checkbox
                    checked={selectedArticles.includes(article.id)}
                    onChange={() => handleSelectArticle(article.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Box>
                <CardActionArea 
                  onClick={() => navigate(`/articles/${article.id}`)}
                  sx={{ flex: 1, display: 'flex' }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' }, 
                    width: '100%'
                  }}>
                    {article.hero_image_url && (
                      <Box sx={{ 
                        width: { xs: '100%', sm: 140 }, 
                        height: { xs: 200, sm: 140 },
                        position: 'relative'
                      }}>
                        <AdaptiveImage
                          imageUrl={article.hero_image_url}
                          alt={article.title}
                          sx={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </Box>
                    )}
                    <CardContent sx={{ flex: 1 }}>
                      <Typography gutterBottom variant="h6" component="h2">
                        {article.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Chip 
                          label={getTypeLabel(article.type)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        {article.subtype && (
                          <Chip 
                            label={getSubtypeLabel(article.subtype)}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ opacity: 0.8 }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Chip 
                          label={article.topic}
                          size="small"
                        />
                        <Chip 
                          label={getStatusLabel(article.status)}
                          color={getStatusColor(article.status)}
                          size="small"
                        />
                        {article.tags?.map((tag) => (
                          <Chip 
                            key={tag}
                            label={tag}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Created {formatDistanceToNow(new Date(article.created_at))} ago
                      </Typography>
                    </CardContent>
                  </Box>
                </CardActionArea>
                <Box sx={{ 
                  p: 2, 
                  borderLeft: { sm: 1 },
                  borderTop: { xs: 1, sm: 0 },
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: { xs: 'row', sm: 'column' },
                  alignItems: 'center',
                  justifyContent: { xs: 'space-between', sm: 'center' },
                  gap: 1
                }}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`/articles/${article.id}`, '_blank');
                    }}
                    size="small"
                    sx={{ color: 'text.secondary' }}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={article.status}
                      label="Status"
                      onChange={(e) => handleStatusChange(article.id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {Object.entries(ARTICLE_STATUSES).map(([value, { label }]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={addTagDialogOpen} onClose={() => setAddTagDialogOpen(false)}>
        <DialogTitle>Add Tag to Selected Articles</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Tag"
            fullWidth
            variant="outlined"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddTagDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddTag} 
            variant="contained" 
            disabled={!newTag.trim()}
          >
            Add Tag
          </Button>
        </DialogActions>
      </Dialog>

      <CreateArticleDialog 
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onArticleCreated={handleArticleCreated}
        projectId={projectId}
      />
    </Stack>
  );
};

export default ArticlesList;
